import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, X } from "lucide-react";

export default function AboutEditor() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const imgInput = useRef(null);

  useEffect(() => {
    api.get("/about").then((res) => {
      setName(res.data.name || "");
      setBody(res.data.body || "");
      setImage(res.data.image || "");
    });
  }, []);

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImage(data.url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/about", { name: name.trim(), body: body.trim(), image: image || null });
      toast.success("Sezione 'Chi sono' aggiornata");
      navigate("/admin");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/admin" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors">
            <ArrowLeft size={15} strokeWidth={1.5} /> Torna
          </Link>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-about-button"
            className="rounded-none bg-[#1C1917] hover:bg-[#9F583F] text-white label-ui px-6 py-5 h-auto transition-colors"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : "Salva"}
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <p className="label-ui text-[#9F583F] mb-2">Chi sono</p>
        <h1 className="font-display text-4xl tracking-tight text-[#1C1917] mb-10">Modifica la tua presentazione</h1>

        <label className="label-ui text-[#57534E] block mb-3">Immagine</label>
        {image ? (
          <div className="relative w-56 mb-10">
            <img src={mediaUrl(image)} alt="Ritratto" className="w-56 aspect-[4/5] object-cover" data-testid="about-image-preview" />
            <button
              onClick={() => setImage("")}
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white text-[#1C1917] transition-colors"
              data-testid="remove-about-image"
            >
              <X size={15} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => imgInput.current?.click()}
            data-testid="upload-about-image"
            className="w-56 aspect-[4/5] border border-dashed border-[#E7E5E4] flex flex-col items-center justify-center gap-2 text-[#57534E] hover:border-[#9F583F] hover:text-[#9F583F] transition-colors mb-10"
          >
            {uploading ? <Loader2 size={24} className="animate-spin" /> : (<><ImagePlus size={24} strokeWidth={1.5} /><span className="label-ui">Carica immagine</span></>)}
          </button>
        )}
        <input ref={imgInput} type="file" accept="image/*" className="hidden" onChange={handleImage} />

        <label className="label-ui text-[#57534E] block mb-3">Nome mostrato</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="about-name-input"
          className="font-display text-2xl rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-10 h-auto py-2"
        />

        <label className="label-ui text-[#57534E] block mb-3">
          Testo (lascia una riga vuota per separare i paragrafi; usa **parola** per il grassetto)
        </label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid="about-body-input"
          rows={10}
          className="font-body text-lg leading-relaxed rounded-none border border-[#E7E5E4] bg-transparent focus-visible:ring-0 focus-visible:border-[#9F583F] resize-y"
        />
      </div>
    </div>
  );
}
